import React, { useEffect, useState, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaFortAwesome, FaTicketAlt, FaGolfBall } from "react-icons/fa";
import { FiArchive, FiCreditCard, FiChevronDown, FiInfo, FiPlay, FiLayers } from "react-icons/fi";
import "./Tickete.css";
import QRModal from "./QRModal";
import TicketDetailsModal from "./TicketDetailsModal";
import BottomSheet from "../../components/common/BottomSheet";
import { supabase } from "../../supabase";
import { toTicketShape, toKlippekortCardShape } from "../../utils/ticketMapping";
import { generateTicketCode } from "./ticketCodeUtils";
import { computeTicketRemainingSeconds, computeTicketEndTimeLabel, normalizeTimestampMs, capPlaySecondsAtClosing } from "./ticketTime";
import {
  isGolfLikeTicket,
  isRoundBased,
  isGroupRound,
  getGroupSize,
  isRoundTicketUsed,
  isGroupRoundFullyUsed,
  isRoundFullyUsed,
  isRoundPlaying,
  TESTING_MODE,
} from "./roundTicket";
import { useParkSettings, todayHours, secondsUntilClosing } from "../../utils/parkSettings";

export default function Tickets() {
  const { t } = useTranslation('tickets');
  const parkSettings = useParkSettings();
  // Read through a ref so the countdown effects always see the latest settings
  const parkSettingsRef = useRef(parkSettings);
  parkSettingsRef.current = parkSettings;
  const TICKETS_CACHE_KEY = 'pws_tickets_cache';
  const cachedTickets = (() => { try { return JSON.parse(localStorage.getItem(TICKETS_CACHE_KEY)) || []; } catch { return []; } })();
  const [searchParams] = useSearchParams();

  const [tickets, setTickets] = useState(cachedTickets);
  const [countdowns, setCountdowns] = useState({});
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [linkedTickets, setLinkedTickets] = useState(null); // Bulk "link for shared printing" selection
  const [linkedCode, setLinkedCode] = useState(null); // One shared manual code for the linked group
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsGroup, setDetailsGroup] = useState(null); // Other tickets bulk-activated alongside selectedTicket, if any
  const [finishConfirmTicket, setFinishConfirmTicket] = useState(null); // Round ticket pending "Fullfør" confirmation
  const [loading, setLoading] = useState(cachedTickets.length === 0);
  const [user, setUser] = useState(null);
  const [refundedTickets, setRefundedTickets] = useState({});
  const [selectedTab, setSelectedTab] = useState(searchParams.get('tab') === 'archive' ? 'archive' : 'main'); // 'main' or 'archive'
  const [archiveVisibleCount, setArchiveVisibleCount] = useState(5);
  const ARCHIVE_PAGE_SIZE = 5;
  const [justActivatedAt, setJustActivatedAt] = useState(null); // activatedAt timestamp of the ticket/group just started, for the highlight pulse
  const [showInfoSheet, setShowInfoSheet] = useState(false); // "How this page works" explainer
  const activeSectionRef = useRef(null);
  const intervalsRef = useRef({});
  const initializedTicketsRef = useRef(new Set()); // Track which tickets have had countdowns initialized
  const hasCachedTicketsRef = useRef(cachedTickets.length > 0);
  const ticketsRef = useRef(cachedTickets); // Mirror of tickets state for use in async callbacks
  const navigate = useNavigate();

  // Recovery: a completed Vipps payment leaves orderReference in localStorage
  // until PaymentReturn finalizes it. If it's still there, the redirect back never
  // landed on PaymentReturn (e.g. iOS PWA opens Safari instead) — finish it now.
  // Only after a grace period though - PaymentReturn's own status check is a real
  // network round-trip to Vipps, so a reference set moments ago (checkout just
  // cancelled/finished, PaymentReturn is still resolving) isn't orphaned yet.
  // Without this, landing on Tickets while that check is still in flight hijacked
  // you straight into the cancelled/success screen instead of your tickets.
  useEffect(() => {
    const pendingReference = localStorage.getItem("orderReference");
    if (!pendingReference) return;
    const setAt = parseInt(localStorage.getItem("orderReferenceSetAt") || "0", 10);
    const age = Date.now() - setAt;
    if (!setAt || age > 20000) {
      navigate('/PaymentReturn');
    }
  }, [navigate]);

  const normalizeActivatedAtMs = normalizeTimestampMs;

  // Loads this user's tickets + klippekort (both the not-yet-redeemed cards and
  // their individual redemptions) directly from Postgres, then flattens them into
  // the same ticket-like shape the rest of this component already expects.
  const loadTickets = useCallback(async (authUserId) => {
    const { data: userRow } = await supabase.from('users').select('id').eq('auth_user_id', authUserId).maybeSingle();
    if (!userRow) { setTickets([]); setLoading(false); return null; }

    const [{ data: ticketRows }, { data: kkRows }] = await Promise.all([
      supabase.from('tickets').select('*').eq('user_id', userRow.id),
      supabase.from('klippekort').select('*').eq('user_id', userRow.id),
    ]);

    const orderIds = [...new Set([
      ...(ticketRows || []).map(t => t.order_id),
      ...(kkRows || []).map(k => k.order_id),
    ])];
    const { data: orderRows } = orderIds.length
      ? await supabase.from('orders').select('id, buyer_name, order_reference').in('id', orderIds)
      : { data: [] };
    const ordersById = new Map((orderRows || []).map(o => [o.id, o]));

    const mappedTickets = (ticketRows || [])
      .map(row => {
        const order = ordersById.get(row.order_id);
        const shaped = toTicketShape(row);
        // Only include activation data if the ticket is still active or recently
        // expired (within 1 minute) - a long-stale activation would otherwise
        // project a bogus end time for a ticket that's actually just archived.
        if (shaped.activatedAt && shaped.totalDuration) {
          const elapsed = Math.floor((Date.now() - normalizeActivatedAtMs(shaped.activatedAt)) / 1000);
          if (shaped.totalDuration - elapsed < -60) {
            shaped.activatedAt = undefined;
            shaped.totalDuration = undefined;
            shaped.printedAt = undefined;
          }
        }
        // A used klippekort clip is an ordinary ticket row with klippekort_id set.
        // Play clips keep the "redeemed klippekort" shape this page already
        // renders (purple, "Lek 1 time", own archive section); minigolf clips
        // stay round tickets.
        const clip = row.klippekort_id && row.category === 'lek'
          ? { category: 'klippekort', type: 'stampCardTicket', usedStamps: 1 }
          : {};
        return { ...shaped, ...clip, customerName: order?.buyer_name, orderReference: order?.order_reference, fromKlippekort: Boolean(row.klippekort_id) };
      })
      .filter(t => (t.category === "lek" && t.type === "ticket") || isGolfLikeTicket(t) || t.fromKlippekort);

    // Not-yet-redeemed klippekort cards, shown as a "(N klipp igjen)" entry.
    const mappedKlippekortCards = (kkRows || [])
      .map(row => {
        const shaped = toKlippekortCardShape(row);
        if (shaped.stampsLeft <= 0) return null;
        const order = ordersById.get(row.order_id);
        return {
          id: row.id,
          name: t('klippekortCardName', { name: shaped.name, count: shaped.stampsLeft }),
          category: 'klippekort',
          type: 'stampCardTicket',
          stampAmounts: shaped.stampsLeft,
          customerName: order?.buyer_name,
          orderReference: order?.order_reference,
          datePurchased: shaped.datePurchased,
        };
      })
      .filter(Boolean);

    const allTickets = [...mappedTickets, ...mappedKlippekortCards];
    ticketsRef.current = allTickets;
    setTickets(allTickets);
    try { localStorage.setItem(TICKETS_CACHE_KEY, JSON.stringify(allTickets)); } catch {}
    setLoading(false);
    return userRow.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Realtime: subscribe directly to this user's own ticket/klippekort rows, so an
  // activation from any source (this device, another device, or the kiosk) fires
  // a native change event immediately - no parent-order "wake up listeners" bump
  // field and no polling needed, unlike the Firestore version of this page.
  useEffect(() => {
    let channels = [];
    let cancelled = false;
    // onAuthStateChange can re-fire before a previous invocation's async
    // teardown/setup finished (e.g. a failed token refresh on tab focus fires
    // several events in quick succession) - removeChannel() is async and
    // un-awaited would let a stale invocation call .on() on a channel a newer
    // invocation already subscribed under the same topic name, which throws.
    let runId = 0;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const myRunId = ++runId;
      const currentUser = session?.user || null;
      setUser(currentUser);
      setLoading(!hasCachedTicketsRef.current);

      await Promise.all(channels.map(ch => supabase.removeChannel(ch)));
      channels = [];
      if (cancelled || myRunId !== runId) return;

      if (currentUser) {
        const dbUserId = await loadTickets(currentUser.id);
        if (cancelled || myRunId !== runId) return;
        if (dbUserId) {
          const refresh = () => loadTickets(currentUser.id);
          channels = [
            supabase.channel('own_tickets_changes')
              .on('postgres_changes', { event: '*', schema: 'public', table: 'tickets', filter: `user_id=eq.${dbUserId}` }, refresh)
              .subscribe(),
            supabase.channel('own_klippekort_changes')
              .on('postgres_changes', { event: '*', schema: 'public', table: 'klippekort', filter: `user_id=eq.${dbUserId}` }, refresh)
              .subscribe(),
          ];
        }
      } else {
        // Not logged in: no real tickets to show. This used to fall back to a
        // legacy localStorage cache (pre-dating Firestore-backed guest checkout),
        // which showed stale/leftover cart or redeemed-klippekort entries as if
        // they were real active tickets - real guest tickets are emailed directly
        // and never visited through this page at all, so that fallback is dead.
        localStorage.removeItem(TICKETS_CACHE_KEY);
        setTickets([]);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      channels.forEach(ch => supabase.removeChannel(ch));
    };
  }, [loadTickets]);


  // Load fully-refunded order flags (per-ticket refund flags come straight from
  // toTicketShape's `refunded` field already, no separate fetch needed for those).
  const loadRefundedTickets = useCallback(async () => {
    const refundedItems = JSON.parse(localStorage.getItem("refundedItems") || "{}");
    const refundedOrders = JSON.parse(localStorage.getItem("refundedOrders") || "{}");

    if (user) {
      try {
        const { data: userRow } = await supabase.from('users').select('id').eq('auth_user_id', user.id).maybeSingle();
        const { data: orderRows } = userRow
          ? await supabase.from('orders').select('order_reference, fully_refunded').eq('user_id', userRow.id)
          : { data: [] };

        const dbRefundedOrders = {};
        (orderRows || []).forEach(order => {
          if (order.fully_refunded) dbRefundedOrders[order.order_reference] = true;
        });

        const mergedRefundedOrders = { ...refundedOrders, ...dbRefundedOrders };
        setRefundedTickets({ items: refundedItems, orders: mergedRefundedOrders });
        localStorage.setItem("refundedOrders", JSON.stringify(mergedRefundedOrders));
      } catch (error) {
        console.error("Error fetching refund data:", error);
        setRefundedTickets({ items: refundedItems, orders: refundedOrders });
      }
    } else {
      setRefundedTickets({ items: refundedItems, orders: refundedOrders });
    }
  }, [user]);

  // Save countdown state to localStorage
  const saveCountdownState = (countdownData) => {
    localStorage.setItem("ticketCountdowns", JSON.stringify(countdownData));
  };

  // Restore countdown state from localStorage
  const restoreCountdownState = useCallback(() => {
    const saved = localStorage.getItem("ticketCountdowns");
    if (saved) {
      const countdownData = JSON.parse(saved);
      const currentTime = Date.now();
      const restoredCountdowns = {};

      Object.entries(countdownData).forEach(([ticketId, data]) => {
        const elapsed = Math.floor((currentTime - data.startedAt) / 1000);
        let remaining = data.totalDuration - elapsed;
        
        // Check if this is a day pass and if we need to cap to closing time (unless testing mode)
        if (data.isDayPass && !TESTING_MODE) {
          const secondsUntilClosing = getSecondsUntilClosing();
          if (secondsUntilClosing <= 0) {
            // Park is closed, remove this countdown
            const currentSaved = JSON.parse(localStorage.getItem("ticketCountdowns") || "{}");
            delete currentSaved[ticketId];
            saveCountdownState(currentSaved);
            return;
          }
          // Cap remaining time to closing time
          remaining = Math.min(remaining, secondsUntilClosing);
        }
        
        if (remaining > 0) {
          restoredCountdowns[ticketId] = remaining;
          initializedTicketsRef.current.add(ticketId); // Mark as initialized
          // Restart the interval for this countdown
          intervalsRef.current[ticketId] = setInterval(() => {
            setCountdowns(prev => {
              const newVal = (prev[ticketId] || 0) - 1;
              
              // For day pass tickets, also check closing time
              if (data.isDayPass && !TESTING_MODE) {
                const secondsUntilClosing = getSecondsUntilClosing();
                if (secondsUntilClosing <= 0 || newVal <= 0) {
                  clearInterval(intervalsRef.current[ticketId]);
                  delete intervalsRef.current[ticketId];
                  initializedTicketsRef.current.delete(ticketId); // Remove from initialized set
                  const currentSaved = JSON.parse(localStorage.getItem("ticketCountdowns") || "{}");
                  delete currentSaved[ticketId];
                  saveCountdownState(currentSaved);
                  return { ...prev, [ticketId]: 0 };
                }
              } else if (newVal <= 0) {
                clearInterval(intervalsRef.current[ticketId]);
                delete intervalsRef.current[ticketId];
                initializedTicketsRef.current.delete(ticketId); // Remove from initialized set
                const currentSaved = JSON.parse(localStorage.getItem("ticketCountdowns") || "{}");
                delete currentSaved[ticketId];
                saveCountdownState(currentSaved);
                return { ...prev, [ticketId]: 0 };
              }
              return { ...prev, [ticketId]: newVal };
            });
          }, 1000);
        } else {
          // Timer already finished, remove from localStorage
          const currentSaved = JSON.parse(localStorage.getItem("ticketCountdowns") || "{}");
          delete currentSaved[ticketId];
          saveCountdownState(currentSaved);
        }
      });

      setCountdowns(restoredCountdowns);
    }
  }, []);

  // Restore countdown state from localStorage when component mounts
  useEffect(() => {
    restoreCountdownState();
    loadRefundedTickets();
    
    // Set up interval to check for refund updates every 5 seconds
    const refundCheckInterval = setInterval(() => {
      loadRefundedTickets();
    }, 5000);

    return () => clearInterval(refundCheckInterval);
  }, [loadRefundedTickets, restoreCountdownState]);


  // Separate useEffect to restore Firebase countdowns when tickets change
  useEffect(() => {
    if (tickets.length === 0) return;
    
    const currentTime = Date.now();
    const firebaseCountdowns = {};
    let hasNewInitializations = false;
    
    tickets.forEach(ticket => {
      // Process timed tickets that should show countdown in app (ordinary + redeemed klippekort)
      const isTimedTicket =
        (ticket.category === 'lek' && ticket.type === 'ticket') ||
        (ticket.category === 'klippekort' && ticket.type === 'stampCardTicket' && typeof ticket.usedStamps === 'number' && ticket.usedStamps > 0);
      if (!isTimedTicket) return;
      
      // Check if ticket was activated (from kiosk or manual start)
      if (ticket.activatedAt && ticket.totalDuration) {
        const activatedAtMs = normalizeActivatedAtMs(ticket.activatedAt);
        const totalDurationSeconds = Number(ticket.totalDuration);
        if (!activatedAtMs || !Number.isFinite(totalDurationSeconds) || totalDurationSeconds <= 0) {
          return;
        }

        const elapsed = Math.floor((currentTime - activatedAtMs) / 1000);
        let remaining = totalDurationSeconds - elapsed;
        
        // Check if this is a day pass and cap to closing time (unless testing mode)
        const isDayPass = ticket.name && ticket.name.toLowerCase().includes('lek hele dagen');
        if (isDayPass && !TESTING_MODE) {
          const secondsUntilClosing = getSecondsUntilClosing();
          if (secondsUntilClosing <= 0) {
            firebaseCountdowns[ticket.id] = 0;
            return;
          }
          // Cap remaining time to closing time
          remaining = Math.min(remaining, secondsUntilClosing);
        }
        
        if (remaining > 0) {
          if (!initializedTicketsRef.current.has(ticket.id)) {
            initializedTicketsRef.current.add(ticket.id);
            hasNewInitializations = true;
          }
          firebaseCountdowns[ticket.id] = remaining;
        } else {
          firebaseCountdowns[ticket.id] = 0;
          // Clear any existing interval
          if (intervalsRef.current[ticket.id]) {
            clearInterval(intervalsRef.current[ticket.id]);
            delete intervalsRef.current[ticket.id];
            initializedTicketsRef.current.delete(ticket.id);
          }
        }
      }
    });
    
    if (Object.keys(firebaseCountdowns).length > 0) {
      setCountdowns(prev => ({ ...prev, ...firebaseCountdowns }));
    }

    // Ensure externally activated tickets (e.g. kiosk print) are persisted for restore logic.
    if (hasNewInitializations) {
      const currentSaved = JSON.parse(localStorage.getItem("ticketCountdowns") || "{}");
      tickets.forEach((ticket) => {
        if (!initializedTicketsRef.current.has(ticket.id)) return;
        const activatedAtMs = normalizeActivatedAtMs(ticket.activatedAt);
        const totalDurationSeconds = Number(ticket.totalDuration);
        if (!activatedAtMs || !Number.isFinite(totalDurationSeconds) || totalDurationSeconds <= 0) return;

        const isDayPass = ticket.name && ticket.name.toLowerCase().includes('lek hele dagen');
        currentSaved[ticket.id] = {
          startedAt: activatedAtMs,
          totalDuration: totalDurationSeconds,
          isDayPass: isDayPass,
        };
      });
      saveCountdownState(currentSaved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  // SEPARATE effect: Start intervals for initialized tickets
  useEffect(() => {
    // Check which initialized tickets don't have intervals yet
    initializedTicketsRef.current.forEach(ticketId => {
      // Skip if interval already exists
      if (intervalsRef.current[ticketId]) {
        return;
      }

      // Find the ticket to get day pass info
      const ticket = tickets.find(t => t.id === ticketId && t.category === 'lek' && t.type === 'ticket');
      if (!ticket) return;

      const isDayPass = ticket.name && ticket.name.toLowerCase().includes('lek hele dagen');
      
      intervalsRef.current[ticketId] = setInterval(() => {
        setCountdowns(prev => {
          const newVal = (prev[ticketId] || 0) - 1;
          
          // For day pass tickets, also check closing time
          if (isDayPass && !TESTING_MODE) {
            const secondsUntilClosing = getSecondsUntilClosing();
            if (secondsUntilClosing <= 0 || newVal <= 0) {
              clearInterval(intervalsRef.current[ticketId]);
              delete intervalsRef.current[ticketId];
              initializedTicketsRef.current.delete(ticketId);
              return { ...prev, [ticketId]: 0 };
            }
          } else if (newVal <= 0) {
            clearInterval(intervalsRef.current[ticketId]);
            delete intervalsRef.current[ticketId];
            initializedTicketsRef.current.delete(ticketId);
            return { ...prev, [ticketId]: 0 };
          }
          return { ...prev, [ticketId]: newVal };
        });
      }, 1000);
    });
  }, [countdowns, tickets]); // Watch both - run when countdowns change (new ticket) or tickets list changes

  // Seconds until today's closing time from the park settings (/park in the
  // admin console); 0 after closing or on a closed day
  const getSecondsUntilClosing = () => secondsUntilClosing(parkSettingsRef.current, new Date());

  // Check if a ticket is refunded
  const isTicketRefunded = (ticket) => {
    // This exact ticket doc was refunded (set directly by the admin refund flow).
    if (ticket?.refunded === true) return true;
    // Whole order refunded - unambiguous, safe to hide every ticket in it.
    if (refundedTickets.orders && refundedTickets.orders[ticket.orderReference]) {
      return true;
    }
    // Deliberately NOT checking refundedTickets.items (the old name-keyed refund
    // counter) here: it counts refunds per item *name*, not per ticket, so with 2+
    // tickets sharing a name it can't tell which one was refunded - checking it here
    // would hide every same-named ticket, not just the refunded one. The backend still
    // writes it for the admin console's own bookkeeping, just not used for this check.
    return false;
  };


  // Start countdown for a ticket. sharedStartTime lets a bulk activation give every
  // ticket in the batch the exact same start (and thus end time), instead of each one
  // stamping its own Date.now() after sequential Firestore writes drift them apart.
  const startCountdown = async (ticket, sharedStartTime) => {
    // Check if ticket is refunded
    if (isTicketRefunded(ticket)) {
      toast.error(t('toastRefundedCantActivate'));
      return;
    }
    
    // Only bail if countdown is actively running (interval exists AND remaining > 0).
    // Do NOT bail just because countdowns map has a 0 or stale value.
    if (intervalsRef.current[ticket.id] && (countdowns[ticket.id] || 0) > 0) {
      return;
    }
    
    // Check if this is a day pass ticket
    const isDayPass = ticket.name && ticket.name.toLowerCase().includes('lek hele dagen');
    
    // Same 10-min bonus the kiosk print path already adds (gui-server.js) -
    // this activation path had been computing duration alone, so an in-app
    // activation ran short compared to a printed ticket for the same order.
    let totalDuration = (Number(ticket.duration) + (Number(ticket.bonusMinutes) || 0)) * 60;
    
    // A day pass can't start after closing, and no play ticket runs past closing
    // (same end time as the printed Pi label) - unless in testing mode
    if (!TESTING_MODE) {
      const secondsUntilClosing = getSecondsUntilClosing();
      if (isDayPass && secondsUntilClosing <= 0) {
        const today = todayHours(parkSettings, new Date());
        toast.error(today.closed && today.reason ? t('toastClosedToday', { reason: today.reason }) : t('toastParkClosed'));
        return;
      }
      totalDuration = capPlaySecondsAtClosing(totalDuration, secondsUntilClosing);
    }
    
    const startTime = sharedStartTime ?? Date.now();

    // Mark ticket as initialized BEFORE setting state to prevent race condition
    initializedTicketsRef.current.add(ticket.id);
    setCountdowns(prev => ({
      ...prev,
      [ticket.id]: totalDuration,
    }));

    // Optimistic update so groupActiveTickets (keyed on activatedAt) can combine a
    // bulk-activated batch into one stacked card immediately, instead of waiting on
    // the write + Realtime round-trip for every ticket in the batch - that
    // round-trip lag was the visible ~2s delay before cards merged. The write
    // below still happens; this just doesn't block the UI on it.
    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, activatedAt: startTime, totalDuration } : t));

    // Save countdown data to localStorage
    const currentSaved = JSON.parse(localStorage.getItem("ticketCountdowns") || "{}");
    currentSaved[ticket.id] = {
      startedAt: startTime,
      totalDuration: totalDuration,
      isDayPass: isDayPass, // Store day pass flag for restore
    };
    saveCountdownState(currentSaved);

    // Save activation time if user is logged in - ticket.id is the row's own
    // Postgres id (used klippekort clips are ordinary tickets rows too).
    if (user && ticket.orderReference) {
      try {
        const { error } = await supabase.from('tickets').update({
          activated_at: new Date(startTime).toISOString(),
          total_duration: totalDuration,
          activated_from: 'app',
        }).eq('id', ticket.id);
        if (error) throw error;
        return startTime;
      } catch (error) {
        console.error('Error saving activation:', error);
      }
    }

    return startTime;
  };

  // Start every ticket in a group (all bought in the same order) together, with one
  // shared start/end time - used for the "N billetter" stacked card's single action,
  // so a 4-ticket purchase activates and prints as one unit instead of 4 separate steps.
  const startCountdownForGroup = async (ticketsToStart) => {
    const sharedStartTime = Date.now();
    // Fire every ticket's activation without awaiting each one in turn - startCountdown/
    // activateRoundTicket both apply their optimistic UI update synchronously before their
    // first await, so kicking them all off back-to-back (instead of one at a time) lands
    // every update in the same render instead of the group visibly splitting apart
    // ticket-by-ticket while earlier ones are still mid-Firestore-write.
    const activations = ticketsToStart.map(ticket =>
      isRoundBased(ticket)
        ? (!Boolean(ticket.isPlaying) ? activateRoundTicket(ticket, sharedStartTime) : Promise.resolve())
        : startCountdown(ticket, sharedStartTime)
    );
    await Promise.all(activations);
    scrollToActiveAndHighlight(sharedStartTime);
  };

  // Jump back to the "Aktiv" section and pulse the just-started card/stack, same
  // highlight-pulse treatment as a just-redeemed reward in Coupons.jsx. startTime
  // is the ticket's (or bulk group's) activatedAt, which is exactly what
  // groupActiveTickets keys stacked cards on, so it doubles as the highlight key.
  function scrollToActiveAndHighlight(startTime) {
    if (!startTime) return;
    setJustActivatedAt(startTime);
    // Wait two frames so the newly-activated card has already reflowed into the Aktiv
    // section before measuring - scrolling in the same tick as the state update targets
    // the pre-reflow position, which then shifts under the in-flight smooth-scroll and
    // shows up as a slight downward jitter mid-pulse.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        activeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  // Clear the just-activated highlight after the pulse has had time to play
  useEffect(() => {
    if (!justActivatedAt) return;
    const timer = setTimeout(() => setJustActivatedAt(null), 2500);
    return () => clearTimeout(timer);
  }, [justActivatedAt]);

  // Clean up intervals on unmount
  useEffect(() => {
    const intervals = intervalsRef.current; // capture ref value
    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, []);


  function getTicketRemainingSeconds(ticket) {
    const localRemaining = countdowns[ticket?.id];
    if (localRemaining !== undefined && Number.isFinite(localRemaining)) {
      return localRemaining;
    }

    // Fallback (no local countdown override yet): compute from Firestore fields directly.
    return computeTicketRemainingSeconds(ticket);
  }

  // Static end-time label (e.g. "14:35") instead of a live countdown, so it never drifts out of sync.
  function getTicketEndTimeLabel(ticket) {
    return computeTicketEndTimeLabel(ticket);
  }


  function isTicketStarted(ticket) {
    return getTicketRemainingSeconds(ticket) > 0;
  }

  function isTicketArchived(ticket) {
    const hasEverStarted = Boolean(ticket?.activatedAt) || countdowns[ticket?.id] !== undefined;
    if (!hasEverStarted) return false;
    return getTicketRemainingSeconds(ticket) <= 0;
  }

  // Single round: activate → isPlaying: true
  const activateRoundTicket = async (ticket, sharedStartTime) => {
    if (!user) return;
    const startTime = sharedStartTime ?? Date.now();
    // Optimistic update
    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, isPlaying: true, activatedAt: startTime } : t));
    try {
      const { error } = await supabase.from('tickets').update({
        is_playing: true, activated_at: new Date(startTime).toISOString(),
      }).eq('id', ticket.id);
      if (error) throw error;
    } catch (err) { console.error('activateRoundTicket error:', err); }
    return startTime;
  };

  // Manually mark playing round(s) as finished, so the player doesn't have to wait for
  // closing-time auto-archiving to move them out of the Active section. Accepts a single
  // ticket or a bulk-activated group (all finished together).
  const finishRoundTickets = async (ticketsToFinish) => {
    setFinishConfirmTicket(null);
    const usedAt = Date.now();
    const ids = new Set(ticketsToFinish.map(t => t.id));
    setTickets(prev => prev.map(t => ids.has(t.id) ? { ...t, isPlaying: false, usedAt } : t));
    if (!user) return;
    const { error } = await supabase.from('tickets')
      .update({ is_playing: false, used_at: new Date(usedAt).toISOString() })
      .in('id', [...ids]);
    if (error) console.error('finishRoundTickets error:', error);
  };

  // Render a round-based ticket card (golf/minigolf) — single or group
  function renderRoundTicketCard(ticket) {
    const { icon, color: baseColor } = getTicketIcon(ticket);
    const color = ticket.fromKlippekort ? 'purple' : baseColor;
    const refunded = isTicketRefunded(ticket);

    if (isGroupRound(ticket)) {
      const used = isGroupRoundFullyUsed(ticket);
      const playing = !used && Boolean(ticket.isPlaying);

      let badgeClass = used ? 'expired' : playing ? 'running' : 'inactive';
      let badgeLabel = used ? t('usedLabel') : playing ? t('activeLabel') : t('notActivatedLabel');
      if (refunded) { badgeClass = 'refunded'; badgeLabel = t('refundedLabel'); }

      return (
        <div
          key={ticket.id}
          className={`t-card${playing ? ' t-card--active' : used ? ' t-card--expired' : ''}${isJustActivated(ticket) ? ' t-card--pulse' : ''}`}
        >
          <div className="t-header">
            <div className="t-icon-wrap">
              <div className={`t-icon t-icon--${color}`}>{icon}</div>
              <div>
                <div className="t-title">{ticket.name}</div>
                <div className="t-subtitle">{t('playersCount', { count: getGroupSize(ticket) })}</div>
              </div>
            </div>
            <span className={`t-badge t-badge--${badgeClass}`}>{badgeLabel}</span>
          </div>
          <div className={`t-timer${playing ? ' t-timer--running' : ''}`}>
            <span className="t-timer-label">{t('statusLabel')}</span>
            <span className={`t-timer-value${!playing ? ' t-timer-value--inactive' : ''}`}>
              {refunded ? t('refundedLabel') : used ? t('usedLabel') : playing ? t('activeRoundLabel') : t('readyLabel')}
            </span>
          </div>
          {!used && !refunded && (
            <div className="t-actions">
              {!playing && (
                <button className="t-btn-start" onClick={async () => scrollToActiveAndHighlight(await activateRoundTicket(ticket))}>
                  {t('activateLabel')}
                </button>
              )}
              {playing && (
                <button className="t-btn-start" onClick={() => openQRModal(ticket)}>
                  {t('showTicketLabel')}
                </button>
              )}
              {playing && (
                <button className="t-btn-finish" onClick={() => setFinishConfirmTicket([ticket])}>
                  {t('finishLabel')}
                </button>
              )}
              <button className="t-btn-details" onClick={() => openDetailsModal(ticket)}>
                {t('detailsLabel')}
              </button>
            </div>
          )}
          {(used || refunded) && (
            <div className="t-actions">
              <button className="t-btn-details" style={{ flex: 1 }} onClick={() => openDetailsModal(ticket)}>
                {t('detailsLabel')}
              </button>
            </div>
          )}
        </div>
      );
    }

    // Single round ticket
    const used = isRoundTicketUsed(ticket);
    const playing = !used && Boolean(ticket.isPlaying);
    const isKlippekort = Boolean(ticket.fromKlippekort);

    let badgeClass = used ? 'expired' : playing ? 'running' : 'inactive';
    let badgeLabel = used ? t('usedLabel') : playing ? t('activeLabel') : t('notActivatedLabel');
    if (refunded) { badgeClass = 'refunded'; badgeLabel = t('refundedLabel'); }

    return (
      <div
        key={ticket.id}
        className={`t-card${playing ? ' t-card--active' : used ? ' t-card--expired' : ''}${isKlippekort ? ' t-card--klippekort' : ''}${isJustActivated(ticket) ? ' t-card--pulse' : ''}`}
      >
        <div className="t-header">
          <div className="t-icon-wrap">
            <div className={`t-icon t-icon--${color}`}>{icon}</div>
            <div>
              <div className="t-title">{getTicketDisplayTitle(ticket)}</div>
            </div>
          </div>
          <span className={`t-badge t-badge--${badgeClass}`}>{badgeLabel}</span>
        </div>
        <div className={`t-timer${playing ? ' t-timer--running' : ''}`}>
          <span className="t-timer-label">{t('statusLabel')}</span>
          <span className={`t-timer-value${!playing ? ' t-timer-value--inactive' : ''}`}>
            {refunded ? t('refundedLabel') : used ? t('usedLabel') : playing ? t('activeRoundLabel') : t('readyLabel')}
          </span>
        </div>
        {!used && !refunded && (
          <div className="t-actions">
            {!playing && (
              <button className="t-btn-start" onClick={async () => scrollToActiveAndHighlight(await activateRoundTicket(ticket))}>
                {t('activateLabel')}
              </button>
            )}
            {playing && (
              <button className="t-btn-start" onClick={() => openQRModal(ticket)}>
                {t('showTicketLabel')}
              </button>
            )}
            {playing && (
              <button className="t-btn-finish" onClick={() => setFinishConfirmTicket([ticket])}>
                {t('finishLabel')}
              </button>
            )}
            <button className="t-btn-details" onClick={() => openDetailsModal(ticket)}>
              {t('detailsLabel')}
            </button>
          </div>
        )}
        {(used || refunded) && (
          <div className="t-actions">
            <button className="t-btn-details" style={{ flex: 1 }} onClick={() => openDetailsModal(ticket)}>
              {t('detailsLabel')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Open QR modal for specific ticket
  const openQRModal = (ticket) => {
    setSelectedTicket(ticket);
    setShowQRModal(true);
  };

  // Open one shared QR + manual code for a group of tickets (kiosk prints each on one scan).
  // The code is a hash of the sorted ticket IDs - deterministic, so reopening the same
  // group always gives the same code. A tiny lookup doc lets the kiosk resolve it server-side.
  const openLinkedQRModalFor = async (ticketsArray) => {
    const ids = ticketsArray.map(t => t.id).sort();
    const code = generateTicketCode(ids.join('|'));
    setLinkedTickets(ticketsArray);
    setLinkedCode(code);
    setShowQRModal(true);
    try {
      const { error } = await supabase.from('ticket_links').upsert({ code, ticket_ids: ids });
      if (error) throw error;
    } catch (error) {
      console.error('Error saving ticket link:', error);
    }
  };

  // Close QR modal
  const closeQRModal = () => {
    setShowQRModal(false);
    setSelectedTicket(null);
    setLinkedTickets(null);
    setLinkedCode(null);
  };

  const openDetailsModal = (ticket, group) => {
    setSelectedTicket(ticket);
    setDetailsGroup(group || null);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailsGroup(null);
  };

  // Helper: icon + color for ticket category
  function getTicketIcon(ticket) {
    const name = (ticket.name || '').toLowerCase();
    if (name.includes('golf')) return { icon: <FaGolfBall size={22} />, color: 'green' };
    if (ticket.category === 'klippekort') return { icon: <FaTicketAlt size={22} />, color: 'purple' };
    return { icon: <FaFortAwesome size={22} />, color: 'orange' };
  }

  // Klippekort-redeemed tickets carry a raw name like "Klippekort 10 - Minigolf" or
  // "Lek 1 time (2 klipp)" - no need to repeat "klippekort" in the title, just the activity.
  function getTicketDisplayTitle(ticket) {
    if (ticket.category === 'klippekort') {
      const hours = Number(ticket.usedStamps) || 1;
      return t('displayTitlePlay', { hours, unit: t(hours === 1 ? 'hourUnitSingular' : 'hourUnitPlural') });
    }
    if (ticket.fromKlippekort) return t('displayTitleMinigolf');
    return (ticket.name || '').replace(/\s*\(\d+\s*klipp( igjen)?\)/gi, '').trim();
  }

  // Same key groupActiveTickets uses below, so this matches whichever ticket(s)
  // were just started - whether alone or as part of a bulk group.
  function isJustActivated(ticket) {
    return justActivatedAt != null && normalizeActivatedAtMs(ticket.activatedAt) === justActivatedAt;
  }

  // Bucket a ticket list by some shared key (e.g. activatedAt, orderReference) into
  // groups - a ticket whose key is missing/falsy always gets its own singleton group
  // rather than being merged with other keyless tickets.
  function groupTicketsByKey(list, keyFn) {
    const groups = [];
    const byKey = new Map();
    for (const t of list) {
      const key = keyFn(t);
      if (!key) { groups.push([t]); continue; }
      const existing = byKey.get(key);
      if (existing) existing.push(t);
      else { const group = [t]; byKey.set(key, group); groups.push(group); }
    }
    return groups;
  }

  // Active tickets that share the exact same activatedAt (i.e. activated together, whether
  // bulk-started or bought together and auto-started as one group) render as one stacked
  // "folder" card instead of N identical-looking cards.
  function groupActiveTickets(activeList) {
    return groupTicketsByKey(activeList, t => normalizeActivatedAtMs(t.activatedAt));
  }

  // Not-yet-started tickets from the same purchase (order) group into one card too, so a
  // 4-ticket order shows and activates as a single unit instead of 4 separate cards.
  function groupInactiveTickets(inactiveList) {
    return groupTicketsByKey(inactiveList, t => t.orderReference);
  }

  // Render the active section as single cards (using the right renderer per ticket type),
  // or one stacked card for a group bulk-activated together - round and timed tickets can
  // mix in the same stack since they were selected and started together.
  function renderActiveTickets(activeList) {
    // Most recently activated first, so a ticket someone just started doesn't get
    // buried below ones that have been running for a while.
    const sorted = [...activeList].sort(
      (a, b) => (normalizeActivatedAtMs(b.activatedAt) || 0) - (normalizeActivatedAtMs(a.activatedAt) || 0)
    );
    return groupActiveTickets(sorted).flatMap(group => {
      if (group.length > 1) return [renderStackedTicketCard(group)];
      const only = group[0];
      return [isRoundBased(only) ? renderRoundTicketCard(only) : renderTicketCard(only)];
    });
  }

  function renderStackedTicketCard(group) {
    const first = group[0];
    const { icon, color: baseColor } = getTicketIcon(first);
    const isKlippekort = group.every(t => t.category === 'klippekort' || t.fromKlippekort);
    const color = isKlippekort ? 'purple' : baseColor;
    const firstTitle = getTicketDisplayTitle(first);
    const sameName = group.every(t => getTicketDisplayTitle(t) === firstTitle);
    const allRoundBased = group.every(t => isRoundBased(t));
    const firstTimed = group.find(t => !isRoundBased(t)) || first;
    const endTimeLabel = getTicketEndTimeLabel(firstTimed);

    return (
      <div className="t-stack" key={`stack-${group.map(t => t.id).join('-')}`}>
        <div className="t-stack-shadow t-stack-shadow--2" />
        <div className="t-stack-shadow t-stack-shadow--1" />
        <div className={`t-card t-card--stacked${isKlippekort ? ' t-card--klippekort' : ''}${isJustActivated(first) ? ' t-card--pulse' : ''}`}>
          <div className="t-header">
            <div className="t-icon-wrap">
              <div className={`t-icon t-icon--${color}`}>{icon}</div>
              <div>
                <div className="t-title">{sameName ? firstTitle : t('ticketsCountTitle', { count: group.length })}</div>
                <div className="t-subtitle">
                  {isKlippekort
                    ? group.map(t => getTicketDisplayTitle(t)).join(', ')
                    : (sameName ? t('ticketsActivatedTogether', { count: group.length }) : group.map(t => getTicketDisplayTitle(t)).join(', '))}
                </div>
              </div>
            </div>
            <span className="t-badge t-badge--running">{t('activeLabel')}</span>
          </div>
          <div className="t-timer t-timer--running">
            <span className="t-timer-label">{allRoundBased ? t('statusLabel') : t('endsAtLabel')}</span>
            <span className="t-timer-value">{allRoundBased ? t('activeRoundLabel') : (endTimeLabel || '—')}</span>
          </div>
          <div className="t-actions">
            <button className="t-btn-start" onClick={() => openLinkedQRModalFor(group)}>
              {t('showNTickets', { count: group.length })}
            </button>
            {allRoundBased && (
              <button className="t-btn-finish" onClick={() => setFinishConfirmTicket(group)}>
                {t('finishLabel')}
              </button>
            )}
            <button className="t-btn-details" onClick={() => openDetailsModal(first, group)}>
              {t('detailsLabel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the not-yet-activated section: tickets bought in the same order group into one
  // stacked card (round and timed tickets can mix, same as the active-stack case), single
  // purchases fall back to their normal individual card.
  function renderInactiveTickets(inactiveList) {
    return groupInactiveTickets(inactiveList).flatMap(group => {
      if (group.length > 1) return [renderStackedInactiveCard(group)];
      const only = group[0];
      return [isRoundBased(only) ? renderRoundTicketCard(only) : renderTicketCard(only)];
    });
  }

  function renderStackedInactiveCard(group) {
    const first = group[0];
    const { icon, color: baseColor } = getTicketIcon(first);
    const isKlippekort = group.every(t => t.category === 'klippekort' || t.fromKlippekort);
    const color = isKlippekort ? 'purple' : baseColor;
    const firstTitle = getTicketDisplayTitle(first);
    const sameName = group.every(t => getTicketDisplayTitle(t) === firstTitle);

    return (
      <div className="t-stack" key={`inactive-stack-${group.map(t => t.id).join('-')}`}>
        <div className="t-stack-shadow t-stack-shadow--2" />
        <div className="t-stack-shadow t-stack-shadow--1" />
        <div className={`t-card t-card--stacked${isKlippekort ? ' t-card--klippekort' : ''}`}>
          <div className="t-header">
            <div className="t-icon-wrap">
              <div className={`t-icon t-icon--${color}`}>{icon}</div>
              <div>
                <div className="t-title">{sameName ? firstTitle : t('ticketsCountTitle', { count: group.length })}</div>
                <div className="t-subtitle">{group.map(tk => getTicketDisplayTitle(tk)).join(', ')}</div>
              </div>
            </div>
            <span className="t-badge t-badge--inactive">{t('notActivatedLabel')}</span>
          </div>
          <div className="t-timer">
            <span className="t-timer-label">{t('statusLabel')}</span>
            <span className="t-timer-value t-timer-value--inactive">{t('readyLabel')}</span>
          </div>
          <div className="t-actions">
            <button className="t-btn-start" onClick={() => startCountdownForGroup(group)}>
              {t('startNTickets', { count: group.length })}
            </button>
            <button className="t-btn-details" onClick={() => openDetailsModal(first, group)}>
              {t('detailsLabel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render a single ticket card matching HTML design
  function renderTicketCard(ticket) {
    const { icon, color } = getTicketIcon(ticket);
    const started = isTicketStarted(ticket);
    const archived = isTicketArchived(ticket);
    const refunded = isTicketRefunded(ticket);
    const endTimeLabel = getTicketEndTimeLabel(ticket);
    const isKlippekort = ticket.category === 'klippekort';

    let badgeClass = 'inactive';
    let badgeLabel = t('notActivatedLabel');
    if (refunded) { badgeClass = 'refunded'; badgeLabel = t('refundedLabel'); }
    else if (started) { badgeClass = 'running'; badgeLabel = t('activeLabel'); }
    else if (archived) { badgeClass = 'expired'; badgeLabel = t('expiredLabel'); }

    return (
      <div
        key={ticket.id}
        className={`t-card${started ? ' t-card--active' : ''}${archived ? ' t-card--expired' : ''}${isKlippekort ? ' t-card--klippekort' : ''}${isJustActivated(ticket) ? ' t-card--pulse' : ''}`}
      >
        {/* Header */}
        <div className="t-header">
          <div className="t-icon-wrap">
            <div className={`t-icon t-icon--${color}`}>{icon}</div>
            <div>
              <div className="t-title">
                {getTicketDisplayTitle(ticket)}
              </div>
            </div>
          </div>
          <span className={`t-badge t-badge--${badgeClass}`}>{badgeLabel}</span>
        </div>

        {/* End time display (static, avoids countdown drift) */}
        {!archived && !refunded && (
          <div className={`t-timer${started ? ' t-timer--running' : ''}`}>
            <span className="t-timer-label">{started ? t('endsAtLabel') : t('statusLabel')}</span>
            {started
              ? <span className="t-timer-value">{endTimeLabel || '—'}</span>
              : <span className="t-timer-value t-timer-value--inactive">{t('readyLabel')}</span>
            }
          </div>
        )}

        {/* Actions */}
        {!archived && !refunded && (
          <div className="t-actions">
            {!started && (
              <button className="t-btn-start" onClick={async () => scrollToActiveAndHighlight(await startCountdown(ticket))}>
                {t('startTicketLabel')}
              </button>
            )}
            {started && (
              <button className="t-btn-start" onClick={() => openQRModal(ticket)}>
                {t('showTicketLabel')}
              </button>
            )}
            <button className="t-btn-details" onClick={() => openDetailsModal(ticket)}>
              {t('detailsLabel')}
            </button>
          </div>
        )}
        {archived && !refunded && (
          <div className="t-actions">
            <button className="t-btn-details" style={{ flex: 1 }} onClick={() => openDetailsModal(ticket)}>
              {t('detailsLabel')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Tab badge count - mirrors Coupons.jsx's "active items" badge.
  const activeCount = tickets.filter(t => {
    const isOrdinary = (t.category === 'lek' && t.type === 'ticket') || isGolfLikeTicket(t);
    const isKlippekortTimed = t.category === 'klippekort' && t.type === 'stampCardTicket' && typeof t.usedStamps === 'number' && t.usedStamps > 0;
    if (!(isOrdinary || isKlippekortTimed) || isTicketRefunded(t)) return false;
    return isRoundBased(t) ? (isRoundPlaying(t) && !isRoundFullyUsed(t)) : isTicketStarted(t);
  }).length;

  return (
    <div className="tickets-page">
      {/* Tab switcher */}
      <div className="t-header-row">
        <div className="t-tabs">
          <button className={`t-tab${selectedTab === 'main' ? ' t-tab--active' : ''}`} onClick={() => setSelectedTab('main')}>
            {t('tabTickets')}
            {activeCount > 0 && <span className="t-tab-badge">{activeCount}</span>}
          </button>
          {selectedTab !== 'archive' && (
            <button className="t-info-btn t-info-btn--purple" onClick={() => navigate('/klippekort-redeem')} aria-label={t('redeemKlippekortLabel')} title={t('redeemKlippekortLabel')}>
              <FiCreditCard size={18} />
            </button>
          )}
          <button
            className={`t-tab t-tab--icon${selectedTab === 'archive' ? ' t-tab--active' : ''}`}
            onClick={() => { setSelectedTab('archive'); setArchiveVisibleCount(ARCHIVE_PAGE_SIZE); }}
            aria-label={t('usedLabel')}
            title={t('usedLabel')}
          >
            <FiArchive size={16} />
          </button>
        </div>
        <button className="t-info-btn" onClick={() => setShowInfoSheet(true)} aria-label={t('howTicketsWorkTitle')} title={t('howTicketsWorkTitle')}>
          <FiInfo size={18} />
        </button>
      </div>

      {loading ? (
        <div className="t-loading">
          <div className="t-spinner" />
          <p>{t('loadingTickets')}</p>
        </div>
      ) : selectedTab === 'main' ? (() => {
        const all = tickets.filter(t =>
          (t.category === 'lek' && t.type === 'ticket') || isGolfLikeTicket(t) ||
          (t.category === 'klippekort' && t.type === 'stampCardTicket' && typeof t.usedStamps === 'number' && t.usedStamps > 0)
        );
        if (all.length === 0) return (
          <div className="t-empty">
            <h3>{t('emptyTicketsTitle')}</h3>
            <p>{t('emptyTicketsDesc')}{!user && t('emptyTicketsLoginHint')}</p>
          </div>
        );
        const roundAll   = all.filter(t => isRoundBased(t));
        const timedAll   = all.filter(t => !isRoundBased(t));
        const active     = timedAll.filter(t => !isTicketRefunded(t) && isTicketStarted(t));
        const inactive   = timedAll.filter(t => !isTicketRefunded(t) && !isTicketStarted(t) && !isTicketArchived(t));
        // Klippekort-redeemed tickets aren't bought together in one purchase the way
        // ordinary/golf tickets are (each stamp is redeemed one at a time, potentially
        // weeks apart, but shares the original card's orderReference) - keep them out
        // of the order-based grouping below so unrelated redemptions don't get stacked.
        const inactiveGroupable = inactive.filter(t => t.category !== 'klippekort');
        const inactiveKlippekort = inactive.filter(t => t.category === 'klippekort');
        // Round tickets: "ready" = not fully used yet
        const roundReady = roundAll.filter(t => !isTicketRefunded(t) && !isRoundFullyUsed(t));
        const roundPlaying    = roundReady.filter(t => isRoundPlaying(t));
        const roundNotStarted = roundReady.filter(t => !isRoundPlaying(t));
        return (
          <div className="t-scroll">
            {(roundPlaying.length > 0 || active.length > 0) && <><div className="t-section-label" ref={activeSectionRef}>{t('sectionActive')}</div>{renderActiveTickets([...roundPlaying, ...active])}</>}
            {(roundNotStarted.length > 0 || inactiveGroupable.length > 0 || inactiveKlippekort.length > 0) && <><div className="t-section-label">{t('sectionNotActivated')}</div>{renderInactiveTickets([...roundNotStarted, ...inactiveGroupable])}{inactiveKlippekort.map(renderTicketCard)}</>}
          </div>
        );
      })() : (() => {
        const ordinaryAll = tickets.filter(t => (t.category === 'lek' && t.type === 'ticket') || isGolfLikeTicket(t));
        const klippekortAll = tickets.filter(t => t.category === 'klippekort' && t.type === 'stampCardTicket' && typeof t.usedStamps === 'number' && t.usedStamps > 0);
        const roundAll = ordinaryAll.filter(t => isRoundBased(t));
        const timedAll = ordinaryAll.filter(t => !isRoundBased(t));
        const archivedTimed = timedAll.filter(t => !isTicketRefunded(t) && isTicketArchived(t));
        const roundUsed = roundAll.filter(t => !isTicketRefunded(t) && isRoundFullyUsed(t));
        const archivedKlippekort = klippekortAll.filter(t => !isTicketRefunded(t) && isTicketArchived(t));
        const combined = [
          ...roundUsed.map(t => ({ kind: 'round', ticket: t })),
          ...archivedTimed.map(t => ({ kind: 'timed', ticket: t })),
          ...archivedKlippekort.map(t => ({ kind: 'timed', ticket: t })),
        ];
        if (combined.length === 0) return (
          <div className="t-empty">
            <h3>{t('emptyArchiveTitle')}</h3>
            <p>{t('emptyArchiveDesc')}</p>
          </div>
        );
        const visible = combined.slice(0, archiveVisibleCount);
        const remaining = combined.length - visible.length;
        return (
          <div className="t-scroll">
            <div className="t-section-label">{t('sectionUsed')}</div>
            {visible.map(({ kind, ticket }) => kind === 'round' ? renderRoundTicketCard(ticket) : renderTicketCard(ticket))}
            {remaining > 0 && (
              <button className="t-select-toggle t-select-toggle--center" onClick={() => setArchiveVisibleCount(c => c + ARCHIVE_PAGE_SIZE)}>
                {t('showMore', { count: remaining })}
                <FiChevronDown size={14} />
              </button>
            )}
          </div>
        );
      })()}

      {showQRModal && linkedTickets && (
        <QRModal isOpen={showQRModal} onClose={closeQRModal} tickets={linkedTickets} linkedCode={linkedCode} />
      )}
      {showQRModal && !linkedTickets && selectedTicket && (
        <QRModal isOpen={showQRModal} onClose={closeQRModal} ticket={selectedTicket} />
      )}
      {showDetailsModal && selectedTicket && (
        <TicketDetailsModal ticket={selectedTicket} tickets={detailsGroup || undefined} onClose={closeDetailsModal} />
      )}
      {showInfoSheet && (
        <BottomSheet
          icon={<FiInfo size={20} />}
          iconColor="blue"
          title={t('howTicketsWorkTitle')}
          onClose={() => setShowInfoSheet(false)}
        >
          <div className="t-info-block">
            <div className="t-info-block-icon"><FiPlay size={16} /></div>
            <div>
              <div className="t-info-block-title">{t('infoActivationTitle')}</div>
              <div className="t-info-block-text">
                {t('infoActivationText')}
              </div>
            </div>
          </div>
          <div className="t-info-block">
            <div className="t-info-block-icon t-info-block-icon--orange"><FiLayers size={16} /></div>
            <div>
              <div className="t-info-block-title">{t('infoBoughtTogetherTitle')}</div>
              <div className="t-info-block-text">
                {t('infoBoughtTogetherText')}
              </div>
            </div>
          </div>
          <div className="t-info-block">
            <div className="t-info-block-icon t-info-block-icon--purple"><FiCreditCard size={16} /></div>
            <div>
              <div className="t-info-block-title">{t('infoKlippekortTitle')}</div>
              <div className="t-info-block-text">
                {t('infoKlippekortText')}
              </div>
            </div>
          </div>
        </BottomSheet>
      )}
      {finishConfirmTicket && (
        <div className="t-confirm-overlay" onClick={() => setFinishConfirmTicket(null)}>
          <div className="t-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="t-confirm-title">{t('confirmFinishTitle')}</div>
            <div className="t-confirm-desc">{finishConfirmTicket.length > 1 ? t('confirmFinishDescPlural') : t('confirmFinishDescSingle')}</div>
            <div className="t-confirm-actions">
              <button className="t-btn-details" onClick={() => setFinishConfirmTicket(null)}>{t('cancelLabel')}</button>
              <button className="t-btn-finish" onClick={() => finishRoundTickets(finishConfirmTicket)}>{t('yesDoneLabel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
